import { useEffect, useRef } from 'react';
import { useApolloClient } from '@apollo/client';
import { GetInvoiceSidebarAmountDueDocument } from '../queries/getInvoiceSidebarAmountDue.query.generated';
import { useWatchInvoicePaymentsSubscription } from '../queries/watchInvoicePayments.subscription.generated';
import { useWatchInvoiceCreditsSubscription } from '../queries/watchInvoiceCredits.subscription.generated';

/** How long the watch waits for more changes before it refetches. */
export const MONEY_CHANGE_REFETCH_DELAY_MS = 1500;

/**
 * Calls `refetch` when the invoice's payments or active credits change: a
 * payment recorded, settling or failing, or a credit added or voided, here or
 * anywhere else. The invoice's charges query skips Apollo's cache, so nothing
 * else would bring those changes onto the screen. The sidebar's Amount Due
 * rows skip it too, so they are refetched with it.
 *
 * Changes within `delayMs` of each other refetch once, so a payment recorded
 * and settled moments apart reloads the invoice once rather than twice. The
 * first answer for an invoice only sets the baseline, and opening another
 * invoice sets a new one, so neither refetches.
 */
export function useRefetchOnInvoiceMoneyChange(
  invoiceId: number | null | undefined,
  refetch: () => unknown,
  delayMs: number = MONEY_CHANGE_REFETCH_DELAY_MS
): void {
  const skip = !invoiceId;
  const variables = { invoiceId: Number(invoiceId) };
  // no-cache, so the watch never writes into the cache other queries read.
  const { data: payments } = useWatchInvoicePaymentsSubscription({ variables, skip, fetchPolicy: 'no-cache' });
  const { data: credits } = useWatchInvoiceCreditsSubscription({ variables, skip, fetchPolicy: 'no-cache' });

  // Each watch keeps its own baseline, so a change on one refetches even
  // while the other has not answered.
  const paymentsSignature = payments ? JSON.stringify(payments.arpayments) : null;
  const creditsSignature = credits ? JSON.stringify(credits.arcredits_aggregate.aggregate) : null;

  const client = useApolloClient();
  const refetchRef = useRef(refetch);
  useEffect(() => {
    refetchRef.current = refetch;
  }, [refetch]);

  // One timer for both watches, restarted by each change and dropped when the
  // invoice changes or the screen closes.
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scheduleRef = useRef(() => {});
  useEffect(() => {
    scheduleRef.current = () => {
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => {
        timer.current = null;
        void refetchRef.current();
        void client.refetchQueries({ include: [GetInvoiceSidebarAmountDueDocument] });
      }, delayMs);
    };
    return () => {
      if (timer.current) clearTimeout(timer.current);
      timer.current = null;
    };
  }, [invoiceId, delayMs, client]);

  useChangeAfterBaseline(invoiceId, paymentsSignature, scheduleRef);
  useChangeAfterBaseline(invoiceId, creditsSignature, scheduleRef);
}

/**
 * Calls `onChangeRef.current` when `signature` changes for the same invoice.
 * The first signature for an invoice is its baseline and calls nothing.
 */
function useChangeAfterBaseline(
  invoiceId: number | null | undefined,
  signature: string | null,
  onChangeRef: { current: () => void }
): void {
  const seen = useRef<{ invoiceId: number; signature: string } | null>(null);
  useEffect(() => {
    if (!invoiceId || signature === null) return;
    const last = seen.current;
    seen.current = { invoiceId, signature };
    if (last && last.invoiceId === invoiceId && last.signature !== signature) onChangeRef.current();
  }, [invoiceId, signature, onChangeRef]);
}
